import { Engagement, EngagementStatus, EngagementStepStatus, isTaskCompleted } from '@aiwyn/api';
import { uniq } from 'lodash';
import { EngagementStepDisplay } from '../../models';

enum EngagementStepInfo {
  NOT_STARTED = 'NOT_STARTED',
  STARTED = 'STARTED',
  COMPLETED = 'COMPLETED'
}

enum EngagementSignatoryInfo {
  SINGLE = 'SINGLE',
  MULTIPLE = 'MULTIPLE',
  LAST_SIGNATORY = 'LAST_SIGNATORY'
}

interface EngagementLandingInfoPayload {
  engagement: Engagement;
  engagementSteps: EngagementStepDisplay[];
  userContactId: number;
}

interface EngagementLandingBuildResponsePayload {
  remainingStepsCount: number;
  signatoryInfo: EngagementSignatoryInfo;
  stepInfo: EngagementStepInfo;
  stepText: string;
}

export enum EngagementLandingView {
  REASSIGNMENT = 'REASSIGNMENT',
  STEPS = 'STEPS',
}

export const EngagementLandingAcknowledgement: Record<EngagementLandingView, string> = {
  [EngagementLandingView.REASSIGNMENT]: 'By confirming, I\'m transferring signing authority to the person named above. Once reassigned, this individual will become the official signer for the engagement.',
  [EngagementLandingView.STEPS]: 'By proceeding, I agree that my electronic signature and initials are legally binding and have the same validity and effect as my handwritten signature and initials on this engagement.',
}
interface EngagementLandingInfoResponse {
  title: string;
  description: string;
}

type EngagementLandingStepType = EngagementLandingView.REASSIGNMENT | EngagementStatus.EXECUTED | EngagementStatus.CANCELED | EngagementStatus.UNDER_REVISION;
type EngagementLandingBaseInfo = Record<EngagementLandingStepType, EngagementLandingInfoResponse | ((engagement: Engagement) => EngagementLandingInfoResponse)>;
export const ENGAGEMENT_LANDING_BASE_INFO: EngagementLandingBaseInfo = {
  [EngagementLandingView.REASSIGNMENT]: (engagement: Engagement) => ({
    title: `Confirm Reassignment of Signature for <span class="text-emphasis">${engagement.name}</span>`,
    description: 'You are reassigning signing authority only and will no longer have access to this engagement after reassignment. The new signer will receive an email invitation to review and sign the engagement. <strong>Billing responsibility cannot be reassigned as part of this action.</strong>',
  }),
  [EngagementStatus.UNDER_REVISION]: {
    title: 'This engagement is currently under revision.',
    description: 'When a new version is ready for review, you will be notified.',
  },
  [EngagementStatus.EXECUTED]: {
    title: 'Your steps are complete!',
    description: 'All other parties have completed their steps. Your engagement has been executed.',
  },
  [EngagementStatus.CANCELED]: {
    title: 'This engagement has been canceled by A-Team Accounting.',
    description: 'Please reach out to the firm if you have any questions.',
  },
};


const titleByStep: Record<EngagementStepInfo, (args: { remainingStepsCount: number; stepText: string }) => string> = {
  [EngagementStepInfo.NOT_STARTED]: () => 'Accept your engagement — complete the <span class="text-emphasis">steps below</span> to get started.',
  [EngagementStepInfo.STARTED]: ({ remainingStepsCount, stepText }) => `Just <span class="text-emphasis">${remainingStepsCount} more ${stepText}</span> to go!`,
  [EngagementStepInfo.COMPLETED]: () => 'Your steps are complete!'
};

const descriptionsByStep: Record<EngagementStepInfo, Record<EngagementSignatoryInfo, string>> = {
  [EngagementStepInfo.NOT_STARTED]: {
    [EngagementSignatoryInfo.SINGLE]: 'Once all steps are complete, your engagement will be executed.',
    [EngagementSignatoryInfo.MULTIPLE]: 'Your engagement will be executed once all signatories have completed their steps.',
    [EngagementSignatoryInfo.LAST_SIGNATORY]: 'All other parties have completed their steps. Once all steps are complete, your engagement will be executed.',
  },
  [EngagementStepInfo.STARTED]: {
    [EngagementSignatoryInfo.SINGLE]: 'Once all steps are complete, your engagement will be executed.',
    [EngagementSignatoryInfo.MULTIPLE]: 'Your engagement will be executed once all signatories have completed their steps.',
    [EngagementSignatoryInfo.LAST_SIGNATORY]: 'All other parties have completed their steps. Once all steps are complete, your engagement will be executed.',
  },
  [EngagementStepInfo.COMPLETED]: {
    [EngagementSignatoryInfo.SINGLE]: 'Your engagement has been executed.',
    [EngagementSignatoryInfo.MULTIPLE]: 'Your engagement will be executed once all signatories have completed their steps.',
    [EngagementSignatoryInfo.LAST_SIGNATORY]: 'All other parties have completed their steps. Your engagement has been executed.',
  },
};
export class EngagementLandingUtil {
  static info = ({ engagementSteps, userContactId }: EngagementLandingInfoPayload): EngagementLandingInfoResponse => {
    const completedSteps = engagementSteps.filter(step => step.status === EngagementStepStatus.COMPLETE);
    const remainingStepsCount = engagementSteps.length - completedSteps.length;
    const stepText = remainingStepsCount !== 1 ? 'steps' : 'step';

    let stepInfo: EngagementStepInfo, signatoryInfo: EngagementSignatoryInfo;
    if (remainingStepsCount === engagementSteps.length) {
      stepInfo = EngagementStepInfo.NOT_STARTED;
    } else if (remainingStepsCount > 0) {
      stepInfo = EngagementStepInfo.STARTED;
    } else {
      stepInfo = EngagementStepInfo.COMPLETED;
    }

    const allEngagementTasks = engagementSteps
      .map(step => step.tasks)
      .reduce((acc, val) => acc.concat(val), []);

    const isMultipleSignatories = uniq(allEngagementTasks.map(task => task.contactId)).length > 1;
    if (isMultipleSignatories) {
      signatoryInfo = EngagementSignatoryInfo.MULTIPLE;

      const isEngagementOpen = stepInfo !== EngagementStepInfo.COMPLETED;

      const isTheLastSignatory = allEngagementTasks
        .filter(task => task.contactId !== userContactId)
        .every(task => isTaskCompleted(task.status));

      if (isEngagementOpen && isTheLastSignatory) {
        signatoryInfo = EngagementSignatoryInfo.LAST_SIGNATORY;
      }

    } else {
      signatoryInfo = EngagementSignatoryInfo.SINGLE;
    }

    return EngagementLandingUtil.retrieveInfoByCurrentValues({ remainingStepsCount, signatoryInfo, stepInfo, stepText })
  };

  static retrieveInfoByCurrentValues({ remainingStepsCount, signatoryInfo, stepInfo, stepText }: EngagementLandingBuildResponsePayload): EngagementLandingInfoResponse {
    const title = titleByStep[stepInfo]({ remainingStepsCount, stepText });
    const description = descriptionsByStep[stepInfo][signatoryInfo];

    return { title, description };
  }
}

